import { useRef } from 'react';
import { useStowagePlanStore } from 'src/context/StowagePlanContext';
import useApiEndpoints from 'src/context/useApiEndpoints';
import useApi from '../useApi';
import { useEffect } from 'react';
import { StowagePlanBM } from 'src/types/BusinessModel/Stowage/StowagePlanBM';
import apiEndpointUtils from 'src/utils/apiEndpointUtils';
import { mapStowagePlans } from 'src/factory/Stowage/StowagePlanFactory';
import { buildQueryParams } from '../endpoints/queryParamsHelper';
import { getStowageDataItems } from 'src/utils/stowage/stowagePlanChangeUtils/stowageDataItemsBuilder';
import useGetStowageApi from './useGetStowageApi';
import usePreloadBaseStowageData from 'src/hooks/stowage/usePreloadBaseStowageData';
import { updateStowageDataSimple } from 'src/utils/stowage/updateStowageDataUtils';
import { Vessel } from 'src/types/ViewModel/Vessel/Vessel';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { getMasterStowagePlan, fillMissingImosSyncDateFromParents } from 'src/utils/stowage/stowagePlanUtils';
import { useVesselStore } from 'src/context/VesselContext';

export interface MasterStowagePlanLoadResult {
    success: boolean;
    data?: StowagePlan;
    error?: unknown;
}

export default () => {
    const { apiEndpoints } = useApiEndpoints();
    const { state, getApiData } = useApi();
    const { setLoadedMasterStowagePlan, setMasterPlanLoadState, setMasterPlanLoadStateImo } = useStowagePlanStore();
    const { getStowagePlanChanges } = useGetStowageApi();
    const { processApiEndpointError } = apiEndpointUtils();
    const { getPreloadedData } = usePreloadBaseStowageData();

    // Track the currently selected vessel so stale async responses can be discarded
    const { selectedVessel } = useVesselStore();
    const currentImoRef = useRef<number | null>(null);
    currentImoRef.current = selectedVessel?.imoNumber ?? null;

    const getMasterStowagePlanFromApi = async (
        vessel: Vessel,
        forceRefreshCache = false,
        isCurrent: () => boolean = () => true
    ): Promise<MasterStowagePlanLoadResult> => {
        const imoNumber = vessel.imoNumber;
        // An IMO alone is not a sufficient stale-work guard: logout or a
        // replacement connection can retain the same selected vessel.
        const canCommit = (): boolean => currentImoRef.current === imoNumber && isCurrent();
        const baseDataResult = await getPreloadedData();
        if (!canCommit()) {
            return { success: false, error: 'Stowage load was superseded' };
        }
        if (!baseDataResult.success) {
            console.error('Failed to load base stowage data:', baseDataResult.error);
            if (canCommit()) {
                setMasterPlanLoadState('error');
                setMasterPlanLoadStateImo(imoNumber);
            }
            return { success: false, error: baseDataResult.error };
        }

        try {
            const result = await getApiData({
                endpoint: apiEndpoints['getMasterStowagePlan'].url,
                queryParam: buildQueryParams({ imoNumber }),
                additionalInfo: { imoNumber },
                forceRefreshCache,
            });

            if (!canCommit()) {
                return { success: false, error: 'Stowage load was superseded' };
            }

            if (!Array.isArray(result) || result.length === 0) {
                if (!canCommit()) {
                    return { success: false, error: 'Stowage load was superseded' };
                }
                setLoadedMasterStowagePlan(null);
                if (!canCommit()) {
                    return { success: false, error: 'Stowage load was superseded' };
                }
                setMasterPlanLoadState('not-found');
                if (!canCommit()) {
                    return { success: false, error: 'Stowage load was superseded' };
                }
                setMasterPlanLoadStateImo(imoNumber);
                console.warn(`No master stowage plan data returned for vessel ${imoNumber}`);
                return { success: false, error: 'No master stowage plan data returned' };
            }

            const bms = result as StowagePlanBM[];
            const _stowagePlans = mapStowagePlans(bms);
            const _masterStowagePlan = getMasterStowagePlan(_stowagePlans);

            if (!_masterStowagePlan) {
                if (!canCommit()) {
                    return { success: false, error: 'Stowage load was superseded' };
                }
                setLoadedMasterStowagePlan(null);
                if (!canCommit()) {
                    return { success: false, error: 'Stowage load was superseded' };
                }
                setMasterPlanLoadState('not-found');
                if (!canCommit()) {
                    return { success: false, error: 'Stowage load was superseded' };
                }
                setMasterPlanLoadStateImo(imoNumber);
                console.warn(`No master stowage plan found for vessel ${imoNumber}`);
                return { success: false, error: 'No master stowage plan found' };
            }

            fillMissingImosSyncDateFromParents(_masterStowagePlan, _stowagePlans);

            const stowagePlanChanges = await getStowagePlanChanges(_masterStowagePlan.stowagePlanGuid, forceRefreshCache);

            if (!canCommit()) {
                return { success: false, error: 'Stowage load was superseded' };
            }

            const { stowagePlanRotationPortCalls, updatedStowagePlan } = getStowageDataItems(stowagePlanChanges, _stowagePlans, _masterStowagePlan);
            if (!canCommit()) {
                return { success: false, error: 'Stowage load was superseded' };
            }
            await updateStowageDataSimple(
                updatedStowagePlan,
                stowagePlanRotationPortCalls,
                updatedStowagePlan.cargoes,
                baseDataResult.data.products,
                baseDataResult.data.productCategories,
                baseDataResult.data.stowageRuleMaps,
                baseDataResult.data.portNames,
                baseDataResult.data.berthNames,
                vessel
            );

            if (!canCommit()) {
                return { success: false, error: 'Stowage load was superseded' };
            }

            setLoadedMasterStowagePlan({ ...updatedStowagePlan });
            if (!canCommit()) {
                return { success: false, error: 'Stowage load was superseded' };
            }
            setMasterPlanLoadState('loaded');
            if (!canCommit()) {
                return { success: false, error: 'Stowage load was superseded' };
            }
            setMasterPlanLoadStateImo(imoNumber);

            return { success: true, data: updatedStowagePlan };
        } catch (error) {
            if (canCommit()) {
                setLoadedMasterStowagePlan(null);
            }
            if (canCommit()) {
                setMasterPlanLoadState('error');
            }
            if (canCommit()) {
                setMasterPlanLoadStateImo(imoNumber);
            }
            console.error('Error loading master stowage plan:', error);
            return { success: false, error };
        }
    };

    useEffect(() => {
        (async () => {
            if (state.status !== 'success') {
                processApiEndpointError(state);
            }
        })();
    }, [state]);

    return { getMasterStowagePlanFromApi };
};
